import { useEffect, useState } from 'react';

import { apiRequest } from '../api/client.js';
import { StatusPill } from './StatusPill.js';
import { HelpHint } from './Tooltip.js';

/**
 * Chat retention for this workspace (ARCHITECTURE.md §31, §35, §65).
 *
 * §35 requires customers be able to view their retention policy. This exists
 * for a more specific reason as well: on a plan that retains nothing, Connaro
 * Chat shows messages arriving in real time and then an empty history on
 * reload. That is correct behaviour and looks exactly like a bug.
 *
 * Without this panel the operator's only conclusion is that Connaro lost their
 * messages.
 */

type Retention = {
  enabled: boolean;
  /** null means unlimited — Infinity is not valid JSON. */
  days: number | null;
  plan: string;
  trialActive: boolean;
  oldestRetainedAt: string | null;
};

export function RetentionPanel(): React.JSX.Element {
  const [retention, setRetention] = useState<Retention | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;

    void apiRequest<Retention>('/chat/retention')
      .then((data) => {
        if (!cancelled) setRetention(data);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  if (failed) return <></>;

  return (
    <section className="panel" aria-labelledby="retention-heading">
      <div className="panel__header">
        <h2 className="panel__title" id="retention-heading">
          Chat history
        </h2>
        {retention === null ? null : (
          <StatusPill
            tone={retention.enabled ? 'ok' : 'idle'}
            label={retention.enabled ? 'Retained' : 'Not retained'}
          />
        )}
      </div>

      <div className="panel__body">
        {retention === null ? (
          <p className="dash__loading" aria-live="polite">
            Loading…
          </p>
        ) : (
          <>
            <dl className="definition">
              <dt>
                Plan
                <HelpHint
                  label="plan"
                  tip="Your plan decides how long chat is kept. It does not affect what arrives live — every connected platform streams in real time regardless."
                />
              </dt>
              <dd>
                {retention.plan}
                {retention.trialActive ? ' (trial)' : ''}
              </dd>

              <dt>Kept for</dt>
              <dd>
                {!retention.enabled
                  ? 'Not kept'
                  : retention.days === null
                    ? 'Kept indefinitely'
                    : `${String(retention.days)} days`}
              </dd>

              {retention.oldestRetainedAt === null ? null : (
                <>
                  <dt>Oldest message</dt>
                  <dd>{new Date(retention.oldestRetainedAt).toLocaleDateString()}</dd>
                </>
              )}
            </dl>

            {retention.enabled ? null : (
              /*
               * The whole reason this panel exists. Said plainly, and framed as
               * the plan's behaviour rather than as a fault — because it is
               * working exactly as designed, and an operator staring at an
               * empty history has no way to know that.
               */
              <p className="callout">
                Messages from your connected platforms still appear in Connaro Chat as they arrive.
                They are not written to storage on this plan, so the history is empty when you
                reload or open Chat later.
              </p>
            )}
          </>
        )}
      </div>
    </section>
  );
}
